import { SpecIDFallback } from '@interfaces/models/spec-id-fallback.enum';
import { createSelector } from '@ngrx/store';
import { PageContent } from '@src/app/secure/review/containers/gifting-review/gifting-review-content-mapper';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import { GiftingRecipient, GiftingState } from '@store/reducers/gifting/gifting.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectSelectedPositions } from '@store/selectors/cost-basis/cost-basis.selectors';
import {
  selectGiftingRecipient,
  selectGiftingState,
  selectSpecIDFallback,
} from '@store/selectors/gifting/gifting.selectors';
import { selectedAccountsFrom } from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';

import {
  buildAccountFromGifting,
  buildAccountToGifting,
  buildAdditionalTaxLot,
  buildGiftReceipt,
  buildSpecIDFallBackMethodReceipt,
  isDollarAmountSpecIdFlow,
} from './cashier-gifting';

export const selectAccountFromGiftingReceipt = (
  editable: boolean = true,
  remoteContent?: PageContent,
) =>
  createSelector(
    selectTriageState,
    selectedAccountsFrom,
    (triageState: TriageState, accountsFrom) =>
      buildAccountFromGifting(triageState, accountsFrom[0], editable, remoteContent),
  );

export const selectAccountToGiftingReceipt = (
  editable: boolean = true,
  remoteContent?: PageContent,
) =>
  createSelector(
    selectGiftingRecipient,
    selectTriageState,
    (recipient: GiftingRecipient, triageState: TriageState) =>
      buildAccountToGifting(recipient, triageState, editable, remoteContent),
  );

export const selectGiftReceipt = (editable: boolean = true, remoteContent?: PageContent) =>
  createSelector(
    selectSelectedPositions,
    selectTriageState,
    selectGiftingState,
    (
      selectedPositions: CostBasisPosition[],
      triageState: TriageState,
      giftingState: GiftingState,
    ) => buildGiftReceipt(selectedPositions, triageState, giftingState, editable, remoteContent),
  );

export const selectAdditionalTaxLotReceipt = (
  editable: boolean = true,
  remoteContent?: PageContent,
) =>
  createSelector(
    selectSelectedPositions,
    selectTriageState,
    (selectedPositions: CostBasisPosition[], triageState: TriageState) =>
      buildAdditionalTaxLot(selectedPositions, triageState, editable, remoteContent),
  );

export const selectIsDollarAmountSpecIdFlow = createSelector(
  selectSelectedPositions,
  (positions: CostBasisPosition[]) =>
    positions?.some((pos) => {
      return isDollarAmountSpecIdFlow(pos);
    }),
);
export const selectGiftingReceiptData = (editable: boolean = true, remoteContent?: PageContent) =>
  createSelector(
    selectAccountFromGiftingReceipt(editable, remoteContent),
    selectGiftReceipt(editable, remoteContent),
    selectAdditionalTaxLotReceipt(editable, remoteContent),
    selectSpecIDFallbackReceipt(editable),
    selectAccountToGiftingReceipt(editable, remoteContent),
    (from, gift, additionalTaxLots, specIdFallback, to) => {
      return {
        from,
        gift,
        additionalTaxLots,
        specIdFallback,
        to,
      };
    },
  );

export const selectSpecIDFallbackReceipt = (editable: boolean = true) =>
  createSelector(selectSpecIDFallback, (specIdFallback: SpecIDFallback) =>
    buildSpecIDFallBackMethodReceipt(specIdFallback, editable),
  );
